import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ImageVideoConfig from "../../results/availability/imagesVideoConfig";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ImagesPreviewVideos from "./imagesPreviewVideos/imagesPreviewVideos";
import { urlTransform, transformRotatorUrls } from "../../../hooks/ImageHook";
import { ImagesPreviewVideo } from "./imagesPreviewVideos/ImagesPreviewVideo";
import i18next from "i18next";
import Icon360View from "presentation/assets/icon360View";
import LightboxGalleryImage from "./lightboxGalleryImage";
import ImageRotator from "../../commons/ImageRotator";
import { useResponsiveScreen } from "../../../hooks/useResponsiveScreen";
import { parseRotatorImages, expandRotatorFramesFromSeed } from "../../../utils/rotatorUtils";
const ImagesCarousel = ({ images, mainImage, type, id }) => {
    const { isMobile } = useResponsiveScreen();
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const urlParams = new URLSearchParams(window.location.search);
    const pickCountry = urlParams.get("pickCountry");
    const dropCountry = urlParams.get("dropCountry");
    const [sentImages, setSentImages] = useState(images);
    const shouldShowVideos = pickCountry === 'US' || dropCountry === 'US';
    const [view360ButtonSize, setView360ButtonSize] = useState(1);
    const sliderRef = useRef(null);
    const containerRef = useRef(null);
    const view360RetryRef = useRef(0);
    const normalizeMediaImage = (image) => {
        if (typeof image === "string") {
            return { type: "image", url: image };
        }
        const normalizedType = String((image === null || image === void 0 ? void 0 : image.type) || "").toLowerCase();
        const declaredAsRotator = normalizedType.includes("rotator");
        const framesFromImages = parseRotatorImages(image === null || image === void 0 ? void 0 : image.images);
        const framesFromUrl = parseRotatorImages(image === null || image === void 0 ? void 0 : image.url);
        const rotatorFrames = framesFromImages.length
            ? framesFromImages
            : (declaredAsRotator || framesFromUrl.length > 1 ? framesFromUrl : []);
        const finalRotatorFrames = declaredAsRotator && rotatorFrames.length <= 1 && (image === null || image === void 0 ? void 0 : image.url)
            ? expandRotatorFramesFromSeed(image.url)
            : rotatorFrames;
        if (declaredAsRotator || finalRotatorFrames.length > 0) {
            return {
                type: "rotator",
                images: finalRotatorFrames,
                url: (image === null || image === void 0 ? void 0 : image.url) || finalRotatorFrames[0] || "",
            };
        }
        return image;
    };
    const normalizeInputImages = (value) => {
        if (Array.isArray(value))
            return value;
        if (typeof value === "string") {
            return parseRotatorImages(value).map((url) => ({ type: "image", url }));
        }
        return [];
    };
    const normalizedImages = useMemo(() => normalizeInputImages(images).map((image) => normalizeMediaImage(image)), [images]);
    useEffect(() => {
        // set sentImages this way, if the image is type "image" add only the url, if is type "360" add the whole object, if is type "video" add the whole object
        let newImages = [];
        normalizedImages.forEach((image) => {
            if (image.type === "image") {
                newImages.push(image.url);
                // 360 disabled for now. Keep this block to re-enable quickly.
                // } else if (image.type === "360") {
                //   newImages.push({
                //     type: "360",
                //     url: `https://cdn.holacarrentals.com/?image=${image.url}`,
                //   });
            }
            else if (image.type === "video" && shouldShowVideos) {
                newImages.push(image);
            }
            else if (image.type === "rotator" && !isMobile) {
                newImages.push({ ...image, images: transformRotatorUrls(image.images || []) });
            }
        });
        setSentImages(newImages);
        const handleResize = () => {
            requestAnimationFrame(updateView360ButtonSize);
        };
        requestAnimationFrame(updateView360ButtonSize);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [normalizedImages, isMobile, shouldShowVideos]);
    const updateView360ButtonSize = useCallback(() => {
        var _a;
        const container = containerRef.current;
        if (!container)
            return;
        const activeSlide = container.querySelector(".slick-current");
        const activeImage = (activeSlide === null || activeSlide === void 0 ? void 0 : activeSlide.querySelector("img")) ||
            container.querySelector(".slick-active img") ||
            container.querySelector(".slick-slide img");
        const activeRect = (_a = activeImage === null || activeImage === void 0 ? void 0 : activeImage.getBoundingClientRect) === null || _a === void 0 ? void 0 : _a.call(activeImage);
        let targetWidth = (activeRect === null || activeRect === void 0 ? void 0 : activeRect.width) || 0;
        let targetHeight = (activeRect === null || activeRect === void 0 ? void 0 : activeRect.height) || 0;
        if (!targetWidth && activeImage) {
            targetWidth = activeImage.offsetWidth || activeImage.naturalWidth || 0;
            targetHeight = activeImage.offsetHeight || activeImage.naturalHeight || 0;
        }
        if (!targetWidth && (activeImage === null || activeImage === void 0 ? void 0 : activeImage.complete) && activeImage.naturalWidth) {
            targetWidth = activeImage.naturalWidth;
            targetHeight = activeImage.naturalHeight || activeImage.naturalWidth;
        }
        if (!targetWidth) {
            if (view360RetryRef.current < 10) {
                view360RetryRef.current += 1;
                requestAnimationFrame(updateView360ButtonSize);
            }
            return;
        }
        view360RetryRef.current = 0;
        const side = Math.min(targetWidth, targetHeight || targetWidth);
        const nextPixelSize = Math.max(10, Math.min(18, side * 0.05));
        const nextSizeRem = Number((nextPixelSize / 16).toFixed(3));
        setView360ButtonSize((previous) => (previous === nextSizeRem ? previous : nextSizeRem));
    }, []);
    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === "undefined")
            return;
        const observer = new ResizeObserver(() => {
            requestAnimationFrame(updateView360ButtonSize);
        });
        observer.observe(container);
        updateView360ButtonSize();
        return () => observer.disconnect();
    }, [updateView360ButtonSize]);
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const observer = new MutationObserver(() => {
            requestAnimationFrame(updateView360ButtonSize);
        });
        observer.observe(container, { childList: true, subtree: true, attributes: true });
        return () => observer.disconnect();
    }, [updateView360ButtonSize]);
    const [isActive, setIsActive] = useState(false);
    const handleTouchClick = () => {
        setIsActive(!isActive);
    };
    const className = `svg-icon-slider_videos ${isActive ? "touched_button_availability" : ""}`;
    let processedImages = normalizedImages.filter((image) => image.url !== mainImage);
    // 360 disabled for now. Remove this line to re-enable 360 items in carousel.
    processedImages = processedImages.filter((image) => image.type !== "360");
    processedImages.push({ type: "image", url: mainImage });
    if (isMobile) {
        processedImages = processedImages.filter((image) => image.type !== "rotator");
    }
    /**
     * Get the poster of the video from the video url
     * @param videoUrl the url of the video
     * @returns returns the poster of the video
     */
    const getPoster = (videoUrl) => {
        const regex = /\/manifest\/[^/]+\.m3u8/;
        return videoUrl.replace(regex, "/thumbnails/thumbnail.jpg?width=1920&height=1080");
    };
    /**
     * Create the video element
     * @param videoUrl the url of the video
     * @param type the type of the video
     * @returns returns the video element
     */
    const videoElement = (videoUrl, type) => {
        return (_jsx(ImagesPreviewVideo, { id: id, src: videoUrl, poster: getPoster(videoUrl) }));
    };
    let settings = {};
    //set the images inside images tags in a new array
    let imagesBig = processedImages.map((image) => {
        if (image.type === "video") {
            return videoElement(image.url, 1);
        }
        else if (image.type === "rotator") {
            return _jsx(ImageRotator, { images: transformRotatorUrls(image.images || []), height: "100%" });
        }
        else {
            return _jsx("img", { src: urlTransform(image.url, 800), alt: "car offer image" });
        }
    });
    imagesBig = imagesBig.reverse();
    let imagesSmall = processedImages.map((image, index) => {
        var _a;
        if (image.type === "video") {
            return (_jsx("div", { className: "video_small_image_details " +
                    (i18next.language.includes("es") ? "hidden" : ""), children: videoElement(image.url, 2) }, index));
        }
        else if (image.type === "rotator") {
            return (_jsx("img", { src: urlTransform(((_a = image.images) === null || _a === void 0 ? void 0 : _a[0]) || '', 200), alt: "car offer image" }, index));
        }
        else {
            return _jsx("img", { src: urlTransform(image.url, 800), alt: "car offer image" }, index);
        }
    });
    let imagesSmallMobile = processedImages.map((image, index) => {
        if (image.type == "video") {
            return (_jsx(_Fragment, { children: _jsx("div", { id: "tri-4", className: "div-triangle " +
                        (i18next.language.includes("es") || !shouldShowVideos
                            ? "hidden"
                            : "") }, index) }));
        }
        else {
            return (_jsx(_Fragment, { children: _jsx("div", { id: "tri-" + index + "-", className: "div-circle", children: " " }, index) }));
        }
    });
    imagesSmallMobile = imagesSmallMobile.reverse();
    imagesSmall = imagesSmall.reverse();
    //push objects to imagesSmall array
    if (type === "availability") {
        imagesSmall.unshift(_jsx("img", { src: urlTransform(mainImage, 800), alt: "car offer image" }));
        imagesBig.unshift(_jsx("img", { src: urlTransform(mainImage, 800), alt: "car offer image" }));
    }
    //remove any duplicate element from the imagesSmall array (skip elements without src, e.g. ImageRotator)
    imagesSmall = imagesSmall.filter((elem, index, self) => { var _a; return !((_a = elem.props) === null || _a === void 0 ? void 0 : _a.src) || index === self.findIndex((t) => { var _a; return ((_a = t.props) === null || _a === void 0 ? void 0 : _a.src) === elem.props.src; }); });
    imagesBig = imagesBig.filter((elem, index, self) => { var _a; return !((_a = elem.props) === null || _a === void 0 ? void 0 : _a.src) || index === self.findIndex((t) => { var _a; return ((_a = t.props) === null || _a === void 0 ? void 0 : _a.src) === elem.props.src; }); });
    ImageVideoConfig();
    const elementsToAdd = i18next.language.includes("es") || !shouldShowVideos
        ? ImagesPreviewVideos(id)
        : [];
    //push to images array 3 video elements but not on thge imagesSmall array
    //if the url of the page containd the word collection push the video elements to the images array
    imagesBig.push(...elementsToAdd);
    function pauseAndResetVideo() {
        //get all video elements
        const videoElements = document.getElementsByTagName("video");
        const videoIcons = document.getElementsByClassName("icon_PlayVideo_carousel");
        //iterate over all video elements
        for (let i = 0; i < videoElements.length; i++) {
            if (videoElements[i].paused === false ||
                videoElements[i].currentTime > 0) {
                videoElements[i].pause();
                videoElements[i].currentTime = 0;
                videoElements[i].load();
                videoElements[i].controls = false;
            }
        }
        //iterate over all video icons
        for (let i = 0; i < videoIcons.length; i++) {
            videoIcons[i].style.display = "block";
        }
    }
    // Event listener to pause and reset video when it loses focus
    if (window.innerWidth > 999) {
        settings = {
            dots: true,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: type == "availability" ? true : false,
            afterChange: (currentSlide) => {
                var _a;
                requestAnimationFrame(updateView360ButtonSize);
                //search the slider with the id that is passed as a prop and then search for the div with the class slick-current inside it and then search for the video element inside it and play it
                const video = (_a = document
                    .getElementById(id)) === null || _a === void 0 ? void 0 : _a.getElementsByClassName("slick-current")[0].getElementsByTagName("video")[0];
                if (video) {
                    video.preload = "metadata";
                }
            },
            beforeChange: (currentSlide) => {
                pauseAndResetVideo();
            },
            customPaging: (i) => (_jsx("div", { className: type === "availability" ? "" : "image_slider_small", children: type === "availability" ? imagesSmallMobile[i] : imagesSmall[i] })),
        };
    }
    else {
        settings = {
            dots: true,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            draggable: true,
            touchMove: true,
            touchThreshold: 100,
            customPaging: (i) => _jsx("a", { children: imagesSmallMobile[i] }),
            afterChange: (currentSlide) => {
                var _a;
                requestAnimationFrame(updateView360ButtonSize);
                //search the slider with the id that is passed as a prop and then search for the div with the class slick-current inside it and then search for the video element inside it and play it
                const video = (_a = document
                    .getElementById(id)) === null || _a === void 0 ? void 0 : _a.getElementsByClassName("slick-current")[0].getElementsByTagName("video")[0];
                if (video) {
                    video.preload = "metadata";
                }
            },
            beforeChange: (currentSlide) => {
                pauseAndResetVideo();
            },
        };
    }
    //replace the content of .slick-prev:before to the IconArrow component
    return (_jsxs(_Fragment, { children: [_jsxs("div", { id: id, className: "lookherediego", ref: containerRef, children: [!isMobile && processedImages.some((img) => img.type === "rotator") && (
                    // 360 disabled for now:
                    // !isMobile && processedImages.some((img: any) => img.type === "360" || img.type === "rotator") && (
                    _jsx("div", { className: "view-360-button", onClick: () => setIsLightboxOpen(true), style: {
                            fontSize: `${view360ButtonSize}rem`,
                        }, children: _jsx(Icon360View, { size: 22 }) })), _jsx(Slider, { ...settings, ref: sliderRef, children: imagesBig.map((image, index) => (_jsx("div", { children: image }, index))) })] }), isLightboxOpen && (_jsx(LightboxGalleryImage, { imagesUrl: sentImages, OGMainImage: mainImage, startOpen: true, openOnRotator: true, onClose: () => setIsLightboxOpen(false) }))] }));
};
export default ImagesCarousel;
